import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Buildings, House, ChartBar, Strategy, X, Key } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import {
  DashboardBlock,
  DashboardBlockHeader,
  DashboardBlockContent,
} from "../layout/DashboardBlock";
import { BlockLoadingState } from "../states/BlockStates";
import { useUnitTypeDistribution } from "hooks/useUnitTypeDistribution";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, getElementAtEvent } from "react-chartjs-2";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

// Colors for unit types
const TYPE_COLORS: Record<string, string> = {
  Residentieel: "#5B7304", // moss
  Commercieel: "#EA8162", // brick
  Overig: "#838C89", // neutral
  Onbekend: "#D1D5DB",
};

// Colors for subtypes - varied palette
const SUBTYPE_COLORS: string[] = [
  "#5B7304", // moss
  "#EA8162", // brick
  "#F59E0B", // amber
  "#3B82F6", // blue
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#14B8A6", // teal
  "#F97316", // orange
  "#6366F1", // indigo
  "#84CC16", // lime
  "#06B6D4", // cyan
  "#EF4444", // red
];

// Strategic Status Colors
const STRATEGIC_STATUS_COLORS: Record<string, string> = {
  Afstoten: "#EA8162",
  "Flexibel Behouden": "#F59E0B",
  "Strategisch Behouden": "#22C55E",
  "Bijzonder behouden": "#5B7304",
};

// Ownership Type Colors
const OWNERSHIP_TYPE_COLORS: Record<string, string> = {
  Huur: "#3B82F6", // blue
  Eigendom: "#22C55E", // green
  "In erfpacht gegeven": "#F59E0B", // amber
  Gebruiksovereenkomst: "#8B5CF6", // violet
  "In concessie gegeven": "#EC4899", // pink
  "Long lease hold": "#14B8A6", // teal
  Verkocht: "#EA8162", // brick
};

interface StrategicStatusMetrics {
  distribution: Array<{ status: string; count: number; buildingIds: string[] }>;
  total: number;
}

interface OwnershipTypeMetrics {
  distribution: Array<{ ownershipType: string; count: number; buildingIds: string[] }>;
  total: number;
}

interface SelectedStatusInfo {
  status: string;
  buildingIds: string[];
}

interface SelectedOwnershipInfo {
  ownershipType: string;
  buildingIds: string[];
}

interface RealEstateMetricsBlockNewProps {
  isLoading?: boolean;
}

export const RealEstateMetricsBlockNew = ({
  isLoading = false,
}: RealEstateMetricsBlockNewProps) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const { metrics, isLoading: loading } = useUnitTypeDistribution();
  const [strategicMetrics, setStrategicMetrics] = useState<StrategicStatusMetrics | null>(null);
  const [strategicLoading, setStrategicLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<SelectedStatusInfo | null>(null);
  const strategicChartRef = useRef<any>(null);

  const [ownershipMetrics, setOwnershipMetrics] = useState<OwnershipTypeMetrics | null>(null);
  const [ownershipLoading, setOwnershipLoading] = useState(true);
  const [selectedOwnership, setSelectedOwnership] = useState<SelectedOwnershipInfo | null>(null);
  const ownershipChartRef = useRef<any>(null);

  // Fetch strategic status metrics
  useEffect(() => {
    async function fetchStrategicMetrics() {
      if (!session) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/building/strategic-status-metrics`,
          { method: "GET" },
          session
        );

        if (response.ok) {
          const data = await response.json();
          setStrategicMetrics(data);
        }
      } catch (error) {
        console.error("Error fetching strategic status metrics:", error);
      } finally {
        setStrategicLoading(false);
      }
    }

    fetchStrategicMetrics();
  }, [session]);

  // Fetch ownership type metrics
  useEffect(() => {
    async function fetchOwnershipMetrics() {
      if (!session) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/building/ownership-type-metrics`,
          { method: "GET" },
          session
        );

        if (response.ok) {
          const data = await response.json();
          setOwnershipMetrics(data);
        }
      } catch (error) {
        console.error("Error fetching ownership type metrics:", error);
      } finally {
        setOwnershipLoading(false);
      }
    }

    fetchOwnershipMetrics();
  }, [session]);

  // Order for strategic status display
  const orderedStrategicStatuses = [
    "Strategisch Behouden",
    "Bijzonder behouden",
    "Flexibel Behouden",
    "Afstoten",
  ];

  // Prepare type distribution data for doughnut chart
  const typeChartData = useMemo(() => {
    if (!metrics || metrics.typeDistribution.length === 0) return null;

    const labels = metrics.typeDistribution.map((t) => t.type);
    const values = metrics.typeDistribution.map((t) => t.count);
    const colors = labels.map((label) => TYPE_COLORS[label] || "#838C89");

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderWidth: 0,
        },
      ],
    };
  }, [metrics]);

  // Prepare subtype distribution data for horizontal bar chart
  const subtypeChartData = useMemo(() => {
    if (!metrics || metrics.subtypeDistribution.length === 0) return null;

    // Take top 10 subtypes
    const topSubtypes = metrics.subtypeDistribution.slice(0, 10);
    const labels = topSubtypes.map((s) => s.subtype);
    const values = topSubtypes.map((s) => s.count);
    const colors = topSubtypes.map((_, index) => SUBTYPE_COLORS[index % SUBTYPE_COLORS.length]);

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderRadius: 4,
          barThickness: 14,
        },
      ],
    };
  }, [metrics]);

  // Prepare strategic status chart data
  const strategicChartData = useMemo(() => {
    if (!strategicMetrics || strategicMetrics.distribution.length === 0) return null;

    // Create a map for easy lookup
    const statusMap = new Map(
      strategicMetrics.distribution.map((item) => [item.status, item.count])
    );

    // Order the data
    const orderedData = orderedStrategicStatuses
      .filter((status) => statusMap.has(status))
      .map((status) => ({
        status,
        count: statusMap.get(status) || 0,
      }));

    const labels = orderedData.map((item) => item.status);
    const values = orderedData.map((item) => item.count);
    const colors = labels.map((label) => STRATEGIC_STATUS_COLORS[label] || "#838C89");

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderColor: colors.map(() => "#ffffff"),
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
    };
  }, [strategicMetrics]);

  // Chart options for strategic status doughnut
  const strategicDoughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "60%",
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: "#5B7304",
          borderWidth: 1,
          padding: 10,
          titleFont: { size: 12 },
          bodyFont: { size: 11 },
          callbacks: {
            label: (context: any) => {
              const value = context.raw;
              const total = strategicMetrics?.total || 0;
              const percentage =
                total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return `${value} gebouwen (${percentage}%)`;
            },
          },
        },
      },
    }),
    [strategicMetrics]
  );

  // Handle strategic status chart click
  const handleStrategicChartClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!strategicChartRef.current || !strategicMetrics) return;

      const elements = getElementAtEvent(strategicChartRef.current, event);
      if (elements.length > 0) {
        const index = elements[0].index;
        const clickedStatus = orderedStrategicStatuses.filter((status) =>
          strategicMetrics.distribution.some((d) => d.status === status)
        )[index];

        const statusData = strategicMetrics.distribution.find(
          (d) => d.status === clickedStatus
        );

        if (statusData) {
          event.preventDefault();
          event.stopPropagation();
          setSelectedStatus({
            status: statusData.status,
            buildingIds: statusData.buildingIds,
          });
        }
      }
    },
    [strategicMetrics]
  );

  // Prepare ownership type chart data
  const ownershipChartData = useMemo(() => {
    if (!ownershipMetrics || ownershipMetrics.distribution.length === 0) return null;

    const labels = ownershipMetrics.distribution.map((item) => item.ownershipType);
    const values = ownershipMetrics.distribution.map((item) => item.count);
    const colors = labels.map((label) => OWNERSHIP_TYPE_COLORS[label] || "#838C89");

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderColor: colors.map(() => "#ffffff"),
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
    };
  }, [ownershipMetrics]);

  // Chart options for ownership type doughnut
  const ownershipDoughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "60%",
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: "#5B7304",
          borderWidth: 1,
          padding: 10,
          titleFont: { size: 12 },
          bodyFont: { size: 11 },
          callbacks: {
            label: (context: any) => {
              const value = context.raw;
              const total = ownershipMetrics?.total || 0;
              const percentage =
                total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return `${value} gebouwen (${percentage}%)`;
            },
          },
        },
      },
    }),
    [ownershipMetrics]
  );

  // Handle ownership type chart click
  const handleOwnershipChartClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!ownershipChartRef.current || !ownershipMetrics) return;

      const elements = getElementAtEvent(ownershipChartRef.current, event);
      if (elements.length > 0) {
        const index = elements[0].index;
        const clickedOwnership = ownershipMetrics.distribution[index];

        if (clickedOwnership) {
          event.preventDefault();
          event.stopPropagation();
          setSelectedOwnership({
            ownershipType: clickedOwnership.ownershipType,
            buildingIds: clickedOwnership.buildingIds,
          });
        }
      }
    },
    [ownershipMetrics]
  );

  // Chart options for doughnut (Type Verdeling) - no legend, we use custom
  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "60%",
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: "#5B7304",
          borderWidth: 1,
          padding: 8,
          titleFont: { size: 11 },
          bodyFont: { size: 10 },
          callbacks: {
            label: (context: any) => {
              const total = context.dataset.data.reduce(
                (a: number, b: number) => a + b,
                0
              );
              const percentage =
                total > 0 ? ((context.raw / total) * 100).toFixed(1) : 0;
              return `${context.label}: ${context.raw} (${percentage}%)`;
            },
          },
        },
      },
    }),
    []
  );

  // Chart options for bar chart
  const barOptions = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: "#5B7304",
          borderWidth: 1,
          padding: 8,
          titleFont: { size: 11 },
          bodyFont: { size: 10 },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            color: "rgba(49, 64, 59, 0.1)",
          },
          ticks: {
            font: { size: 9 },
            stepSize: 10,
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            font: { size: 9 },
          },
        },
      },
    }),
    []
  );

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<Buildings weight="duotone" size={20} />}
        title="Vastgoed Portfolio"
        action={
          <Link
            to="/units"
            className="text-moss-primary text-xs hover:underline"
          >
            Alle units
          </Link>
        }
      />
      <Link to="/units">
        <DashboardBlockContent interactive padding="md">
          {isLoading || loading ? (
            <BlockLoadingState message="Vastgoed gegevens aan het ophalen..." />
          ) : (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="flex items-center justify-between">
                <div className="text-neutral-secondary-mid flex items-center gap-2 text-xs">
                  <House weight="duotone" size={14} />
                  <span>Totaal Units</span>
                </div>
                <span className="rounded bg-moss-secondary-light px-2 py-0.5 text-sm font-semibold text-moss-primary">
                  {metrics?.totalUnits || 0}
                </span>
              </div>

              {/* Unit Type Distribution - Doughnut */}
              {typeChartData && metrics && (
                <div className="space-y-2">
                  <div className="text-neutral-secondary-mid flex items-center gap-2 text-xs">
                    <Buildings weight="duotone" size={14} />
                    <span>Type Verdeling</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="h-[100px] w-[100px] flex-shrink-0">
                      <Doughnut data={typeChartData} options={doughnutOptions} />
                    </div>
                    <div className="flex flex-col gap-1.5 flex-1 pt-1">
                      {metrics.typeDistribution.map((item) => {
                        const total = metrics.totalUnits;
                        const percentage =
                          total > 0
                            ? ((item.count / total) * 100).toFixed(0)
                            : 0;
                        return (
                          <div
                            key={item.type}
                            className="flex items-center justify-between text-xs py-0.5 px-1"
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{
                                  backgroundColor: TYPE_COLORS[item.type] || "#838C89",
                                }}
                              />
                              <span className="text-neutral-secondary-mid">
                                {item.type}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-semibold text-foreground">
                                {item.count}
                              </span>
                              <span className="text-neutral-secondary-mid text-[10px]">
                                ({percentage}%)
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Unit Subtype Distribution - Bar */}
              {subtypeChartData && (
                <div className="space-y-2">
                  <div className="text-neutral-secondary-mid flex items-center gap-2 text-xs">
                    <ChartBar weight="duotone" size={14} />
                    <span>Subtype Verdeling</span>
                  </div>
                  <div className="h-[160px] w-full">
                    <Bar data={subtypeChartData} options={barOptions} />
                  </div>
                </div>
              )}

              {/* Strategic Status Distribution */}
              {!strategicLoading && strategicChartData && strategicMetrics && strategicMetrics.distribution.length > 0 && (
                <div className="space-y-3 pt-3 border-t border-neutral-primary/10">
                  <div className="text-neutral-secondary-mid flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <Strategy weight="duotone" size={14} />
                      <span>Strategische Status</span>
                    </div>
                    <span className="rounded bg-moss-secondary-light px-1.5 py-0.5 text-xs font-medium text-moss-primary">
                      {strategicMetrics.total} gebouwen
                    </span>
                  </div>

                  {/* Selected Status Building IDs Panel */}
                  {selectedStatus && (
                    <div className="bg-neutral-primary/5 rounded-md p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2.5 h-2.5 rounded-full"
                            style={{
                              backgroundColor:
                                STRATEGIC_STATUS_COLORS[selectedStatus.status] || "#838C89",
                            }}
                          />
                          <span className="text-xs font-medium text-foreground">
                            {selectedStatus.status}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedStatus(null);
                          }}
                          className="p-1 hover:bg-neutral-primary/10 rounded transition-colors"
                        >
                          <X size={14} className="text-neutral-secondary-mid" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-[80px] overflow-y-auto">
                        {selectedStatus.buildingIds.map((buildingId) => (
                          <button
                            key={buildingId}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/building/${buildingId}`);
                            }}
                            className="px-2 py-0.5 bg-white rounded text-[10px] font-medium text-moss-primary hover:bg-moss-secondary-light transition-colors border border-moss-primary/20"
                          >
                            {buildingId}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Chart and Legend Row */}
                  <div className="flex items-start gap-4">
                    {/* Doughnut Chart */}
                    <div className="h-[100px] w-[100px] flex-shrink-0 cursor-pointer">
                      <Doughnut
                        ref={strategicChartRef}
                        data={strategicChartData}
                        options={strategicDoughnutOptions}
                        onClick={handleStrategicChartClick}
                      />
                    </div>

                    {/* Legend - clickable */}
                    <div className="flex flex-col gap-1.5 flex-1 pt-1">
                      {orderedStrategicStatuses.map((status) => {
                        const item = strategicMetrics.distribution.find(
                          (d) => d.status === status
                        );
                        if (!item) return null;

                        const percentage =
                          strategicMetrics.total > 0
                            ? ((item.count / strategicMetrics.total) * 100).toFixed(0)
                            : 0;

                        const isSelected = selectedStatus?.status === status;

                        return (
                          <button
                            key={status}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedStatus(
                                isSelected
                                  ? null
                                  : { status: item.status, buildingIds: item.buildingIds }
                              );
                            }}
                            className={`flex items-center justify-between text-xs py-0.5 px-1 rounded transition-colors ${
                              isSelected
                                ? "bg-moss-secondary-light"
                                : "hover:bg-neutral-primary/5"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{
                                  backgroundColor:
                                    STRATEGIC_STATUS_COLORS[status] || "#838C89",
                                }}
                              />
                              <span className="text-neutral-secondary-mid text-left">
                                {status}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-semibold text-foreground">
                                {item.count}
                              </span>
                              <span className="text-neutral-secondary-mid text-[10px]">
                                ({percentage}%)
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <p className="text-[10px] text-neutral-secondary-mid italic text-center">
                    Klik op grafiek of label om gebouwen te zien
                  </p>
                </div>
              )}

              {/* Ownership Type Distribution */}
              {!ownershipLoading && ownershipChartData && ownershipMetrics && ownershipMetrics.distribution.length > 0 && (
                <div className="space-y-3 pt-3 border-t border-neutral-primary/10">
                  <div className="text-neutral-secondary-mid flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <Key weight="duotone" size={14} />
                      <span>Gebouw per Eigendomsrecht</span>
                    </div>
                    <span className="rounded bg-moss-secondary-light px-1.5 py-0.5 text-xs font-medium text-moss-primary">
                      {ownershipMetrics.total} gebouwen
                    </span>
                  </div>

                  {/* Selected Ownership Building IDs Panel */}
                  {selectedOwnership && (
                    <div className="bg-neutral-primary/5 rounded-md p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2.5 h-2.5 rounded-full"
                            style={{
                              backgroundColor:
                                OWNERSHIP_TYPE_COLORS[selectedOwnership.ownershipType] || "#838C89",
                            }}
                          />
                          <span className="text-xs font-medium text-foreground">
                            {selectedOwnership.ownershipType}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedOwnership(null);
                          }}
                          className="p-1 hover:bg-neutral-primary/10 rounded transition-colors"
                        >
                          <X size={14} className="text-neutral-secondary-mid" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-[80px] overflow-y-auto">
                        {selectedOwnership.buildingIds.map((buildingId) => (
                          <button
                            key={buildingId}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/building/${buildingId}`);
                            }}
                            className="px-2 py-0.5 bg-white rounded text-[10px] font-medium text-moss-primary hover:bg-moss-secondary-light transition-colors border border-moss-primary/20"
                          >
                            {buildingId}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Chart and Legend Row */}
                  <div className="flex items-start gap-4">
                    {/* Doughnut Chart */}
                    <div className="h-[100px] w-[100px] flex-shrink-0 cursor-pointer">
                      <Doughnut
                        ref={ownershipChartRef}
                        data={ownershipChartData}
                        options={ownershipDoughnutOptions}
                        onClick={handleOwnershipChartClick}
                      />
                    </div>

                    {/* Legend - clickable */}
                    <div className="flex flex-col gap-1.5 flex-1 pt-1">
                      {ownershipMetrics.distribution.map((item) => {
                        const percentage =
                          ownershipMetrics.total > 0
                            ? ((item.count / ownershipMetrics.total) * 100).toFixed(0)
                            : 0;

                        const isSelected = selectedOwnership?.ownershipType === item.ownershipType;

                        return (
                          <button
                            key={item.ownershipType}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedOwnership(
                                isSelected
                                  ? null
                                  : { ownershipType: item.ownershipType, buildingIds: item.buildingIds }
                              );
                            }}
                            className={`flex items-center justify-between text-xs py-0.5 px-1 rounded transition-colors ${
                              isSelected
                                ? "bg-moss-secondary-light"
                                : "hover:bg-neutral-primary/5"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{
                                  backgroundColor:
                                    OWNERSHIP_TYPE_COLORS[item.ownershipType] || "#838C89",
                                }}
                              />
                              <span className="text-neutral-secondary-mid text-left">
                                {item.ownershipType}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-semibold text-foreground">
                                {item.count}
                              </span>
                              <span className="text-neutral-secondary-mid text-[10px]">
                                ({percentage}%)
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <p className="text-[10px] text-neutral-secondary-mid italic text-center">
                    Klik op grafiek of label om gebouwen te zien
                  </p>
                </div>
              )}

              {/* No data message */}
              {(!metrics || metrics.totalUnits === 0) && !loading && (
                <div className="text-neutral-secondary-mid py-4 text-center text-xs">
                  Geen vastgoed gegevens beschikbaar
                </div>
              )}
            </div>
          )}
        </DashboardBlockContent>
      </Link>
    </DashboardBlock>
  );
};
